import {
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { InternalHoursLog, WeeklyInternalHours } from '@/types'
import { DATE_FORMAT_DMY, DATE_FORMAT_DMY_TIME } from '@/constants'
import { useModal } from '@/hooks'
import { InternalHoursLogModal } from '@/features/internalHours/InternalHoursLogModal'
import { Edit } from 'react-feather'

type Props = {
  week: WeeklyInternalHours
}

export const InternalHoursWeekSection = ({ week }: Props): JSX.Element => {
  const { t } = useTranslation('internalHours')
  const { open } = useModal()

  const handleEdit = (log: WeeklyInternalHours['logs'][number]) => {
    open(<InternalHoursLogModal logData={log as InternalHoursLog} />)
  }

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="subtitle2" fontWeight="bold">
          {t('week.label', { number: week.week_number })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {dayjs(week.week_start).format(DATE_FORMAT_DMY)} &mdash;&nbsp;
          {dayjs(week.week_end).format(DATE_FORMAT_DMY)}
        </Typography>
        <Typography variant="body2" color="secondary.main" fontWeight="bold">
          {week.total_hours}h
        </Typography>
      </Stack>

      {week.logs.length > 0 ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('logs.columns.date')}</TableCell>
                <TableCell>{t('logs.columns.hours')}</TableCell>
                <TableCell>{t('logs.columns.reportType')}</TableCell>
                <TableCell>{t('logs.columns.leaveType')}</TableCell>
                <TableCell>{t('logs.columns.description')}</TableCell>
                <TableCell>{t('logs.columns.createdAt')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {week.logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>
                    {log.date ? dayjs(log.date).format(DATE_FORMAT_DMY) : '-'}
                  </TableCell>
                  <TableCell>{log.amount_hours}</TableCell>
                  <TableCell>
                    {log.report_type
                      ? t(`logs.reportTypes.${log.report_type}`)
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {log.leave_type
                      ? t(`logs.leaveTypes.${log.leave_type}`)
                      : '-'}
                  </TableCell>
                  <TableCell>{log.description || '-'}</TableCell>
                  <TableCell>
                    {log.created_at
                      ? dayjs(log.created_at).format(DATE_FORMAT_DMY_TIME)
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="textThin"
                      endIcon={<Edit size={14} />}
                      onClick={() => handleEdit(log)}
                    >
                      {t('logs.buttons.edit')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
          {t('week.noLogs')}
        </Typography>
      )}
    </Stack>
  )
}
